<template>
  <main>
    <article class="row left-align">
      <section class="col s9">
        <router-link to="/tickeradmin/listservices" class="blue-grey-text text-darken-4 strong">Security Master</router-link>
        >
        <span>Edit Recommendation (Quick)</span>
      </section>
      <section class="col s3 right-align">
        <router-link to="/tickeradmin/editrecmulti" class="blue-grey-text text-darken-4 strong">
          <div class="switch" hidden="true">
            <label>
              Single
              <input type="checkbox">
              <span class="lever" />
              Multiple
            </label>
          </div>
        </router-link>
      </section>
    </article>
    <h4>
      Edit Recommendation
    </h4>
    <article class="container">
      <p v-if="portfolioType === 'real_money'">
        <strong>Note:</strong> Close dates for recommendations in real money portfolios cannot be edited manually.
        For additional context or help, please reach out in the
        <a href="https://fool.slack.com/archives/C0194BY3TGV" target="_blank">
          #investing-tech-help
        </a> Slack channel.
      </p>
    </article>
    <article class="row">
      <autocomplete id="edit-recs-quick-autocomplete"
                    name="Search for a Recommendation"
                    size="s12"
                    :picks="recommendationPicks"
                    watermark="e.g. NFLX Stock Advisor Tom"
                    v-if="recommendationPicks.length"
                    @picked="fetchRecommendation(Number($event.slice(1, $event.indexOf(' '))))"
      />
      <section class="col s12 progress amber accent-1" v-else>
        <aside class="indeterminate amber accent-3" />
      </section>
    </article>
    <aside v-show="recommendation.id">
      <article class="row">
        <section class="input-field col s3">
          <input type="number"
                 placeholder="123456"
                 class="validate"
                 v-model="recommendation.id"
                 pattern="^[0-9]+$"
                 disabled
          >
          <label class="active">Recommendation ID</label>
        </section>
        <section class="input-field col s5">
          <input id="ticker-admin-edit-recs-quick-open-date"
                 type="text" class="datepicker"
                 @change="payload.open_date = $event.target.value"
          >
          <label class="active">Open Date</label>
        </section>
        <section class="input-field col s4">
          <input id="ticker-admin-edit-recs-quick-close-date"
                 type="text" class="datepicker"
                 @change="payload.close_date = $event.target.value"
                 :disabled="portfolioType === 'real_money'"
                 :placeholder="portfolioType === 'real_money' ? 'Only for notional recs' : ''"
          >
          <label class="active">
            Close Date
            <span class="grey-text text-lighten-1">(Optional)</span>
          </label>
        </section>
      </article>
      <article class="row">
        <look-ahead id="usm-edit-recs-quick-instrument-lookahead"
                    name="Instrument"
                    size="s6"
                    :picks="instrumentPicks"
                    @searched="searchInstruments"
                    @picked="payload.instrument_id = Number($event.slice(1, $event.indexOf(' ')))"
                    watermark="AAPL or Apple"
                    :disabled="!$auth.hasRoles('admin')"
        />
        <autocomplete id="usm-edit-recs-quick-portfolio-autocomplete"
                      name="Portfolio"
                      size="s6"
                        :picks="portfolioPicks"
                      watermark="Rule Breakers or @RuleBreakersInclusion"
                      @picked="payload.portfolio_id = Number($event.slice(1, $event.indexOf(' ')))"
                      :disabled="!$auth.hasRoles('admin')"
                      v-if="portfolioPicks.length"
        />
      </article>
      <article class="row">
        <autocomplete id="usm-edit-recs-quick-parent-autocomplete"
                      name="Parent"
                      size="s6"
                      :picks="parentPicks"
                      watermark=""
                      @picked="payload.parent_id = Number($event.slice(1, $event.indexOf(' ')))"
                      :disabled="!$auth.hasRoles('admin')"
                      v-if="parentPicks.length"
        />
        <autocomplete id="usm-edit-recs-quick-corporate-action-autocomplete"
                      name="Corporate Action"
                      size="s6"
                      :picks="corporateActionPicks"
                      watermark=""
                      @picked="payload.spin_off_and_acquisition_id = Number($event.slice(1, $event.indexOf(' ')))"
                      :disabled="!$auth.hasRoles('admin')"
                      v-if="corporateActionPicks.length"
        />
      </article>
      <article class="row">
        <section class="col s9" />
        <section class="col s3">
          <input class="btn waves-effect waves-light teal accent-4 right"
                 type="submit"
                 value="Submit"
                 @click.prevent="saveRecommendation"
          >
        </section>
      </article>
    </aside>
  </main>
</template>

<script>
import axios from 'axios';
import Autocomplete from '@/components/Autocomplete.vue';
import LookAhead from '@/components/LookAhead.vue';

const corporateActionTypes = new Map([
  [0, 'Acquisition'],
  [1, 'Spin Off'],
  [2, 'CapIQT Change'],
]);

const defaultPayload = {
  analyst_id: 0,
  recommendation_id: 0,
  instrument_id: 0,
  portfolio_id: 0,
  open_date: '',
  close_date: '',
  parent_id: null,
  spin_off_and_acquisition_id: null,
};

export default {
  name: 'TickerAdminEditRecQuick',
  components: { Autocomplete, LookAhead },
  props: {},
  data() {
    return {
      portfolios: [],
      recommendations: [],
      instruments: [],
      corporateActions: [],
      recommendation: { instrument: {}, portfolio: {} },
      payload: { ...defaultPayload },
      portfolioType: null,
    };
  },
  computed: {
    recommendationPicks() {
      return this.recommendations.map(recommendation => `#${recommendation.id} ${recommendation.open_date} ${recommendation.instrument.ticker} ${recommendation.portfolio.name}`);
    },
    instrumentPicks() {
      return this.instruments.map(instrument => `#${instrument.InstrumentId} ${instrument.Exchange}:${instrument.Symbol} ${instrument.Name}`);
    },
    portfolioPicks() {
      return this.portfolios.map(portfolio => `#${portfolio.id} ${portfolio.name} (${portfolio.slug})`);
    },
    parentPicks() {
      return this.recommendations.map(recommendation => `#${recommendation.id} ${recommendation.open_date} ${recommendation.instrument.ticker} ${recommendation.portfolio.name}`);
    },
    corporateActionPicks() {
      return this.corporateActions.map(corporateAction => `#${corporateAction.id} ${corporateActionTypes.get(corporateAction.type)} ${corporateAction.old_instrument.ticker} -> ${corporateAction.new_instrument ? corporateAction.new_instrument.ticker : ''}`);
    },
    body() {
      const body = {
        recommendation_id: this.recommendation.id,
        analyst_id: this.$auth.user.pk,
      };
      if (this.payload.instrument_id !== this.recommendation.instrument.id) { body.instrument_id = this.payload.instrument_id; }
      if (this.payload.portfolio_id !== this.recommendation.portfolio.id) { body.portfolio_id = this.payload.portfolio_id; }
      if (this.payload.open_date !== this.recommendation.open_date) { body.open_date = this.payload.open_date; }
      if (this.payload.close_date !== this.recommendation.close_date) { body.close_date = this.payload.close_date || null; }
      if (this.payload.parent_id !== this.recommendation.parent.id) { body.parent_id = this.payload.parent_id; }
      if (this.payload.spin_off_and_acquisition_id !== this.recommendation.corporate_action.id) { body.spin_off_and_acquisition_id = this.payload.spin_off_and_acquisition_id; }
      return body;
    },
  },
  methods: {
    initDatepicker() {
      this.materialize.Datepicker.init(document.querySelectorAll('.datepicker'), {
        autoClose: false,
        format: 'yyyy-mm-dd',
        disableWeekends: true,
        showDaysInNextAndPreviousMonths: true,
        showClearBtn: true,
        onSelect() {},
      });
    },
    async fetchMetadata() {
      const promises = [
        axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/recommendations/portfolios/`),
        axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/corporate_actions/`),
      ];
      const [portfoliosResp, corporateActionsResp] = await Promise.all(promises);
      [this.portfolios, this.corporateActions] =
      [portfoliosResp.data.filter(portfolio => portfolio), corporateActionsResp.data];
    },
    async searchInstruments(query) {
      if (query) {
        const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/quotes/instruments/search/?q=${query}`);
        this.instruments = data.SearchResults;
      }
    },
    async fetchRecommendations() {
      this.recommendations = [];
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/recommendations/recommendations/quick/`);
      this.recommendations = data;
    },
    async fetchRecommendation(recommendationId) {
      const { data } = await axios.get(`${this.$store.getters.VUE_APP_APING_URL}/insights/recommendations/recommendation/?recommendation_id=${recommendationId}`);
      this.recommendation = data;
      this.portfolioType = this.recommendation.portfolio.portfolio_type;
      this.payload.instrument_id = this.recommendation.instrument.id;
      this.payload.portfolio_id = this.recommendation.portfolio.id;
      this.payload.open_date = this.recommendation.open_date;
      this.payload.close_date = this.recommendation.close_date;
      this.payload.parent_id = this.recommendation.parent.id;
      this.payload.spin_off_and_acquisition_id = this.recommendation.corporate_action.id;
      document.getElementById('usm-edit-recs-quick-instrument-lookahead').value = `#${this.recommendation.instrument.id} ${this.recommendation.instrument.ticker} ${this.recommendation.instrument.display_name || this.recommendation.instrument.name}`;
      document.getElementById('usm-edit-recs-quick-portfolio-autocomplete').value = `#${this.recommendation.portfolio.id} ${this.recommendation.portfolio.name} (${this.recommendation.portfolio.slug})`;
      document.getElementById('ticker-admin-edit-recs-quick-open-date').value = this.payload.open_date;
      document.getElementById('ticker-admin-edit-recs-quick-close-date').value = this.payload.close_date;
      document.getElementById('usm-edit-recs-quick-parent-autocomplete').value = this.recommendation.parent.id ? `#${this.recommendation.parent.id} ${this.recommendation.parent.open_date} ${this.recommendation.parent.instrument.ticker} (${this.recommendation.parent.portfolio.name})` : '';
      document.getElementById('usm-edit-recs-quick-corporate-action-autocomplete').value = this.recommendation.corporate_action.id ? `#${this.recommendation.corporate_action.id} ${corporateActionTypes.get(this.recommendation.corporate_action.type)} ${this.recommendation.corporate_action.old_instrument.ticker} -> ${this.recommendation.corporate_action.new_instrument.ticker}` : '';
    },
    async saveRecommendation() {
      if (![
        this.body.analyst_id,
        this.body.recommendation_id,
      ].every(arg => Boolean(arg))) {
        return this.toast('Please populate all required fields', 'pink');
      }
      if (this.body.open_date && new Date(this.body.open_date) > new Date()) {
        return this.toast('Cannot enter an Open Date in the future', 'pink');
      }
      if (this.body.close_date && new Date(this.body.close_date) > new Date()) {
        return this.toast('Cannot enter a Close Date in the future', 'pink');
      }
      if (this.body.open_date && this.body.close_date && new Date(this.body.open_date) > new Date(this.body.close_date)) {
        return this.toast('Cannot enter a Close Date earlier than Open Date', 'pink');
      }
      if (String(Object.keys(this.body)) === String(['recommendation_id', 'analyst_id'])) {
        return this.toast('Please modify at least one field', 'pink');
      }
      const { data } = await axios.put(`${this.$store.getters.VUE_APP_APING_URL}/insights/recommendations/recommendation/`, this.body);
      this.toast('Recommendation saved!', 'green');
      this.payload = { ...defaultPayload };
      document.getElementById('usm-edit-recs-quick-instrument-lookahead').value = '';
      document.getElementById('usm-edit-recs-quick-portfolio-autocomplete').value = '';
      document.getElementById('ticker-admin-edit-recs-quick-open-date').value = '';
      document.getElementById('ticker-admin-edit-recs-quick-close-date').value = '';
      document.getElementById('usm-edit-recs-quick-parent-autocomplete').value = '';
      document.getElementById('usm-edit-recs-quick-corporate-action-autocomplete').value = '';
      this.recommendation = { instrument: {}, portfolio: {} };
      this.fetchRecommendations();
    },
  },
  beforeMount() {
    this.fetchMetadata();
    this.fetchRecommendations();
  },
  mounted() {
    this.initDatepicker();
  },
};
</script>
